import React, { useState, useContext, useEffect } from "react";
import { Button } from "components/button";
import AuthContext from "contexts/AuthContext";
import { isAdminEmail } from "consts/user";
import { Header } from "components/header";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Input } from "components/input";
import { Link } from "react-router-dom";
import LoadingSpinner from "components/loading-spinner";
import ForgotPasswordLink from "components/forgot-password-link";
import PasswordInput from "components/password-input";
import ImpressumFooter from "components/footer-impressum";
import { Helmet } from "react-helmet";
import MessageBanner from "components/message-banner";

const { REACT_APP_API } = process.env;

export const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [userExists, setUserExists] = useState(null);

  const { user, loginHandler, loginError } = useContext(AuthContext);

  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const location = useLocation();

  const redirectTo = new URLSearchParams(location.search).get("redirectTo");

  const { t } = useTranslation();

  useEffect(() => {
    if (isRedirecting && user) {
      const isAdmin = isAdminEmail(user.email);
      if (isAdmin) {
        navigate(redirectTo || t("urls.store_check_in"));
      } else {
        navigate(redirectTo || `${t("urls.my_trade_ins")}${location.search}`);
      }
      setIsLoading(false);
      setIsRedirecting(false);
    }
  }, [isRedirecting, user, navigate, t, redirectTo, location.search]);

  const onSubmit = async () => {
    setIsLoading(t("ui.signing_in"));

    fetch(`${REACT_APP_API}/user/exists/${email}`)
      .then((response) => {
        setUserExists(response.ok);
      })
      .catch(() => {
        setUserExists(false);
      });

    loginHandler(
      { username: email, password },
      {
        onSuccess: (data) => {
          if (data.locationId) {
            localStorage.setItem("locationId", data.locationId);
          }
          setIsRedirecting(true);
        },
      },
      {
        onError: () => {
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Login",
          "description": "Sign in to to track your trade-ins, manage your account, and access your rewards.",
          "url": "https://legobricktakeback.com/en-US/login"
        }
      `}</script>
      </Helmet>
      <div>
        <Header />
        {isLoading ? (
          <LoadingSpinner isLoading={isLoading} />
        ) : (
          <div className="flex items-start pt-24 justify-center min-h-[calc(100vh-81px)]">
            <div className="w-[min(36rem,100%)] px-8">
              <h2 className="font-semibold text-6xl mb-8 leading-normal">
                {t("login.title")}
              </h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="w-full flex flex-col gap-x-large"
              >
                {loginError && (
                  <MessageBanner>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="material-symbols-outlined">info</span>
                      {loginError.message}
                      {!userExists &&
                        ". If you signed up last year, you'll need to sign up again to participate this year."}
                    </div>
                  </MessageBanner>
                )}
                <div>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    label={t("ui.input_email")}
                  />
                </div>
                <div>
                  <PasswordInput
                    label={t("ui.input_password")}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="text-blue text-sm cursor-pointer mt-regular">
                    <ForgotPasswordLink returnToPath={t(`urls.login`)} />
                  </div>
                </div>
                <div className="flex w-full flex-col gap-4">
                  <Button type="submit" onClick={onSubmit}>
                    {t("ui.sign_in")}
                  </Button>
                  <Link
                    to={`${t("urls.trade_in_sign_up")}?returnTo=${t("urls.landing")}&signUpOnly=true`}
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                    >
                      {t("ui.register")}
                    </Button>
                  </Link>
                </div>
              </form>
              <div className="mt-8 text-left flex gap-1 items-start">
                <span
                  className={`material-symbols-outlined scale-75 text-blue align-bottom block`}
                >
                  info
                </span>{" "}
                <div className="text-sm text-gray-500">
                  <Trans
                    i18nKey="login.sign_up_instructions"
                    components={{
                      1: <Link to={t("urls.landing")} className="text-blue" />,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ImpressumFooter />
    </div>
  );
};
